/**
 * AUDIO UPLOAD — `POST /uploads/audio`, for real.
 *
 * The bytes travel as the request body (raw, not multipart — see `http.ts`'s binary-body
 * support), with the filename and title in headers, `encodeURIComponent`-ed because HTTP
 * headers are not safe for arbitrary Unicode. The server re-sniffs the container from the
 * bytes it actually received and asks ffmpeg to decode them before trusting anything the
 * client claimed — see server/src/routes/audioUpload.ts and server/src/services/audioIngest.ts.
 *
 * THE ONE REMAINING BLOCKER is a backend at all: `describeAudioUploadBlockers()` returns
 * `no-backend` when `VITE_API_BASE_URL` is unset (mock mode, or a dev environment nobody
 * has configured yet), and is empty otherwise — object storage and the audio pipeline are
 * both real now (see server/src/services/storage.ts and audioIngest.ts), so they no
 * longer belong on this list.
 *
 * See docs/api-contract.md §24 for the endpoint this implements.
 */
import { API_BASE_URL } from '../config';
import type { AudioContainer } from '../lib/audioUpload';
import { MAX_AUDIO_BYTES } from '../lib/audioUpload';
import { http } from './http';
import type { Song } from '../types/domain';

/**
 * One reason an upload cannot be issued from this application.
 *
 * The same record shape as `LyriaBlocker` in api/lyria.ts, deliberately: the UI already
 * knows how to render a list of these, and a reviewer asking "what is missing?" should
 * get the answer in the same form from every boundary that has one.
 */
export interface AudioUploadBlocker {
  /** Short identifier, for tests and for keys. */
  id: 'no-backend';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/**
 * Everything that has to be true before a file can be stored, and is not.
 *
 * Computed rather than constant so the day `VITE_API_BASE_URL` is set this becomes empty
 * on its own and the panel's notice disappears without an edit. Object storage and the
 * audio pipeline are real now (server/src/services/storage.ts, audioIngest.ts), so
 * "no backend" is the only condition left to check.
 */
export function describeAudioUploadBlockers(): AudioUploadBlocker[] {
  const blockers: AudioUploadBlocker[] = [];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary: 'There is no backend configured, so there is nowhere to send the file.',
      resolution: 'Set VITE_API_BASE_URL. POST /uploads/audio is implemented and waiting.',
    });
  }

  return blockers;
}

/**
 * Thrown by `audioUploadApi.upload`.
 *
 * Its own class rather than `ApiError`, for the reason `UnsupportedCapabilityError` gives:
 * nothing was sent and no status came back, so there is nothing to retry. A caller that
 * cannot tell "the server rejected this" from "this cannot be asked for yet" will offer a
 * Try again button for a permanent condition.
 *
 * Carries the request that WAS built, so a bug report can show exactly what would have
 * gone on the wire — the same courtesy `GenerationUnavailableError` extends.
 */
export class AudioUploadUnavailableError extends Error {
  readonly name = 'AudioUploadUnavailableError';

  /** What would have to change, from describeAudioUploadBlockers(). */
  readonly blockers: AudioUploadBlocker[];

  /** The metadata that would have accompanied the bytes. Never sent. */
  readonly request: AudioUploadRequest | null;

  constructor(
    message: string,
    blockers: AudioUploadBlocker[],
    request: AudioUploadRequest | null = null
  ) {
    super(message);
    this.blockers = blockers;
    this.request = request;
  }
}

/** What the panel knows about a file once lib/audioUpload has looked at it. */
export interface AudioUploadInput {
  /** The file itself. Its bytes are what a real call would stream. */
  file: File;
  /** The container identified by reading the first bytes, not by trusting the name. */
  container: AudioContainer;
  /** Title for the resulting song. Defaults to the filename without its extension. */
  title: string;
  artistName: string;
  genre: string;
  artistGender: string;
  uploaderName: string;
}

/**
 * The metadata half of `POST /uploads/audio`.
 *
 * The bytes travel as the multipart body; this is the JSON part beside them. Built and
 * asserted in tests even though it is never sent, because it is the half of the
 * integration that is genuinely finished — when the endpoint exists, `upload()`'s body
 * becomes a fetch of exactly this and no caller changes.
 *
 * `declaredType` and `sniffedContainer` are BOTH here, and it is not redundancy: the
 * first is what the browser claimed and the second is what the bytes said, they disagree
 * routinely (Safari's `audio/x-m4a` versus Chrome's `audio/mp4`), and a server that
 * re-validates wants to know which of its client's two opinions to distrust.
 */
export interface AudioUploadRequest {
  filename: string;
  /** `File.type`, which is often '' on Windows for FLAC and M4A. */
  declaredType: string;
  /** From lib/audioUpload.sniffAudioContainer. */
  sniffedContainer: AudioContainer;
  byteSize: number;
  title: string;
  artistName: string;
  genre: string;
  artistGender: string;
  uploaderName: string;
  /**
   * The client-side ceiling that was applied, echoed so a server can see it.
   *
   * Not a substitute for the server's own limit — the client's is a courtesy and is
   * trivially bypassed — but a mismatch between the two is worth logging rather than
   * discovering as a confusing rejection.
   */
  clientMaxBytes: number;
}

/**
 * Builds the request body for a validated file.
 *
 * Exported and pure so it can be tested and inspected without a server, matching
 * `generationApi.buildImageMusicRequest`: being able to assert the exact shape is what
 * distinguishes a real boundary from an intention.
 */
export function buildAudioUploadRequest(input: AudioUploadInput): AudioUploadRequest {
  return {
    filename: input.file.name,
    declaredType: input.file.type,
    sniffedContainer: input.container,
    byteSize: input.file.size,
    title: input.title,
    artistName: input.artistName,
    genre: input.genre,
    artistGender: input.artistGender,
    uploaderName: input.uploaderName,
    clientMaxBytes: MAX_AUDIO_BYTES,
  };
}

/** What a caller needs to know before it offers an upload control. */
export interface AudioUploadSupport {
  /** Whether a file can actually be stored right now — true once a backend is configured. */
  available: boolean;
  /** The client-side size ceiling, so the UI states one number from one place. */
  maxBytes: number;
  /** Everything missing, in order. Empty only when uploads genuinely work. */
  blockers: AudioUploadBlocker[];
}

export const audioUploadApi = {
  /**
   * What the upload path can do right now, for the UI to read.
   *
   * The same shape and the same purpose as `generationApi.imageMusicSupport()`: the copy
   * on screen comes from the boundary that knows, rather than from a sentence hardcoded
   * in a component that nobody will update the day a backend appears.
   */
  support(): AudioUploadSupport {
    return {
      available: audioUploadApi.supports(),
      maxBytes: MAX_AUDIO_BYTES,
      blockers: describeAudioUploadBlockers(),
    };
  },

  /**
   * Whether audio can be stored: true once a backend is configured.
   *
   * Derived from the blocker list rather than a separate flag, so it cannot get out of
   * step with what the notice on screen says.
   */
  supports(): boolean {
    return describeAudioUploadBlockers().length === 0;
  },

  /**
   * POST /uploads/audio — the file's bytes as a raw body, metadata in headers.
   *
   * Throws `AudioUploadUnavailableError` when `supports()` is false (no backend
   * configured) rather than attempting a request against `apiUrl()`, which throws its own
   * less specific error — the same guard `generationApi`'s real calls use. Returns the
   * `Song` the server created, ready to add to the library exactly like a generated one.
   */
  async upload(input: AudioUploadInput): Promise<Song> {
    const request = buildAudioUploadRequest(input);
    if (!audioUploadApi.supports()) {
      throw new AudioUploadUnavailableError(
        'Uploading audio is not available yet: there is nowhere to store the file. ' +
          'See src/api/audioUploadApi.ts.',
        describeAudioUploadBlockers(),
        request
      );
    }

    const bytes = await input.file.arrayBuffer();
    return http.post<Song>('/uploads/audio', bytes, {
      headers: {
        // Always this, regardless of what the browser guessed `file.type` was: the
        // server's `express.raw()` only parses a body whose Content-Type matches this
        // exact value (see server/src/routes/audioUpload.ts), and the browser's guess is
        // discarded there anyway — the container is sniffed from the bytes themselves.
        'Content-Type': 'application/octet-stream',
        'X-Upload-Filename': encodeURIComponent(request.filename),
        'X-Upload-Title': encodeURIComponent(request.title),
        'X-Upload-Artist-Name': encodeURIComponent(request.artistName),
        'X-Upload-Genre': encodeURIComponent(request.genre),
        'X-Upload-Artist-Gender': encodeURIComponent(request.artistGender),
        'X-Upload-Uploader-Name': encodeURIComponent(request.uploaderName),
      },
    });
  },
};
